import React from 'react';

import { gql } from '@apollo/client';

import {
  SelfScheduleOptionsModalWrapper_InterviewMeetingLocationFragment,
  SelfScheduleOptionsModalWrapper_JobStageSettingsV2Fragment,
  SelfScheduleOptionsModalWrapper_SelfScheduleFragment,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import AvailabilityRequestedModal from '../CandidateAvailabilityOptions/Options/AvailabilityRequestedModal';

import { SelfScheduleOptionsModal, Props as SelfScheduleOptionsModalProps } from './SelfScheduleOptionsModal';

type Fragments = {
  selfSchedule: SelfScheduleOptionsModalWrapper_SelfScheduleFragment | undefined;
  jobStageSettingsV2: SelfScheduleOptionsModalWrapper_JobStageSettingsV2Fragment | undefined;
  interviewMeetingLocation: SelfScheduleOptionsModalWrapper_InterviewMeetingLocationFragment | undefined;
};

/**
 * Wrapper component for SelfScheduleOptionsModal.
 *
 * This wrapper is used to conditionally render the AvailabilityRequestedModal
 * if the `isAvailabilityRequested` prop is true. This allows the parent component
 * to decide whether to show the availability request modal or the self-schedule options modal
 * based on the current state or user action.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} [props.isAvailabilityRequested] - Flag to determine if the AvailabilityRequestedModal should be shown.
 * @param {Function} props.onClose - Function to handle the close action of the modal.
 * @returns {JSX.Element} The rendered component.
 */
const SelfScheduleOptionsModalWrapper: FCWithFragments<
  Fragments,
  SelfScheduleOptionsModalProps & { isAvailabilityRequested?: boolean }
> = (props) => {
  const { isAvailabilityRequested, onClose } = props;

  if (isAvailabilityRequested) {
    return <AvailabilityRequestedModal onClose={onClose} />;
  }

  return <SelfScheduleOptionsModal {...props} />;
};

SelfScheduleOptionsModalWrapper.fragments = {
  selfSchedule: gql`
    ${SelfScheduleOptionsModal.fragments.selfSchedule}
    fragment SelfScheduleOptionsModalWrapper_selfSchedule on SelfScheduleOptions {
      ...SelfScheduleOptionsModal_selfSchedule
    }
  `,
  jobStageSettingsV2: gql`
    ${SelfScheduleOptionsModal.fragments.jobStageSettingsV2}
    fragment SelfScheduleOptionsModalWrapper_jobStageSettingsV2 on JobStageSettingsV2 {
      ...SelfScheduleOptionsModal_jobStageSettingsV2
    }
  `,
  interviewMeetingLocation: gql`
    ${SelfScheduleOptionsModal.fragments.interviewMeetingLocation}
    fragment SelfScheduleOptionsModalWrapper_interviewMeetingLocation on InterviewMeetingLocationSettingPref {
      ...SelfScheduleOptionsModal_interviewMeetingLocation
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SelfScheduleOptionsUpsertMutation = gql`
  ${SelfScheduleOptionsModalWrapper.fragments?.selfSchedule}
  mutation SelfScheduleOptionsUpsert($input: SelfScheduleOptionsUpsertInput!) {
    selfScheduleOptionsUpsert(input: $input) {
      ...SelfScheduleOptionsModal_selfSchedule
    }
  }
`;

export default SelfScheduleOptionsModalWrapper;
